<template>
    <div
        :id="id"
        class="repeatable-container-wrapper"
        :class="{ error }"
    >
        <template v-if="children.length">
            <template
                v-for="(item, index) in children"
                :key="index"
            >
                <RepeatableContainerItem
                    :children="item"
                    :item-index="index"
                    :parent-id="id"
                    :reveal-behavior="revealBehavior"
                    :initially-toggled="initiallyToggled(index)"
                    :allow-remove="allowAddRemove"
                    :remove-icon="removeIcon"
                    :removing="removeIndexLoading === index"
                    @download-document="emit('download-document', $event)"
                    @remove-item="handleItemRemove(index)"
                />
            </template>
        </template>
        <div
            v-if="allowAddRemove && !hasReachedMaxRepeats"
            class="add-iteration"
        >
            <button
                type="button"
                class="container-minor-btn"
                @click="handleItemAdded"
            >
                <LoadingCircle v-if="addLoading" />
                {{ addLabel || t('repeatable-container.add') }}
            </button>
        </div>
        <ErrorText
            v-if="computedError"
            :error="computedError"
        />
        <template v-if="promptOnRemove">
            <Dialog
                v-model="showRemovePrompt"
                size="sm"
                cancel-button
                cancel-button-class="container-minor-btn"
                :apply-button-text="t('remove')"
                apply-button-class="container-minor-btn warning-button"
                @cancel="handleCancelItemRemove"
                @apply="() => removeItem(removeIndexLoading)"
            >
                <p class="remove-prompt">{{ promptOnRemoveMessage || t('repeatable-container.remove-prompt') }}</p>
            </Dialog>
        </template>
    </div>
</template>

<script setup lang="ts">
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import RepeatableContainerItem from './RepeatableContainerItem.vue'
import { useI18n } from 'widget-components/utilities/i18n'
import {
    type LazyBlockToolsInject,
    type ExtendedBlock,
    type RepeatableContainer,
} from 'widget-components/integrations/CustomPage/types'
import { computed, inject, nextTick, ref } from 'vue'
import { lazyBlockToolsInjectionKey } from 'widget-components/integrations/CustomPage/injection-keys'
import { isNotNil } from 'ramda'
import LoadingCircle from '../LoadingCircle/LoadingCircle.vue'
import Dialog from 'widget-components/atoms/Dialog/Dialog.vue'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    id: string
    children: ExtendedBlock[][]
    revealBehavior?: RepeatableContainer['revealBehavior']
    addLabel?: string
    maxRepeats?: number
    promptOnRemove?: boolean
    promptOnRemoveMessage?: string
    error?: string
    allowAddRemove?: boolean
    removeIcon?: string
    indexPath: number[]
}>()

const {
    getLazyBlockProp,
    dispatchComponentAction,
} = inject(lazyBlockToolsInjectionKey, {
    requestBlockConfig: () => {},
    getLazyBlockProp: () => {} ,
    dispatchComponentAction: async () => {},
} as LazyBlockToolsInject)

const loading = computed(() => addLoading.value || removeIndexLoading.value > -1)
const addLoading = ref(false)
const removeIndexLoading = ref(-1)
const showRemovePrompt = ref(false)

const hasReachedMaxRepeats = computed(() => {
    if (typeof props.maxRepeats !== 'number') {
        return false
    }
    return props.children.length >= props.maxRepeats
})

const emit = defineEmits(['download-document', 'items:add', 'items:remove'])

const { t } = useI18n()

const computedError = computed(() => {
    return getLazyBlockProp<string>(props.id, 'error') || props.error
})

const previousIterationsCount = ref(props.children.length)
const initiallyToggled = (iterationIdx: number) => iterationIdx >= previousIterationsCount.value

const handleItemAdded = async () => {
    if (loading.value) return

    addLoading.value = true
    const newBlock = await dispatchComponentAction('add-section', { repeatableContainerId: props.id, blockId: props.id })

    if (newBlock) {
        emit('items:add', newBlock)
    }

    addLoading.value = false
    void nextTick(() => previousIterationsCount.value = previousIterationsCount.value + 1)
}

const removeItem = async (index: number) => {
    const response = await dispatchComponentAction('remove-section', {
        repeatableContainerId: props.id,
        blockId: props.id,
        sectionId: index,
    })
    if (isNotNil(response)) {
        emit('items:remove', index)
    }

    void nextTick(() => previousIterationsCount.value = previousIterationsCount.value - 1)
    removeIndexLoading.value = -1
}

const handleItemRemove = async (index: number) => {
    if (loading.value) return

    removeIndexLoading.value = index

    if (props.promptOnRemove) {
        showRemovePrompt.value = true
    } else {
        await removeItem(index)
    }
}

const handleCancelItemRemove = async () => {
    removeIndexLoading.value = -1
}

</script>

<style lang="sass" scoped>
@import "./RepeatableContainer.sass"
</style>

